import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  useGetItemById,
  useMutateItem as useMutateItemForWorker,
} from '../../hooks/entityMethodsHook'
import { useAuth0 } from '@auth0/auth0-react'
import Api from '../../lib/api'

export const useGetWorker = (key: string, workerId: any | undefined) => {
  const uri = `users/${workerId}`
  return useGetItemById(key, uri, workerId)
}

export const useGetTimeSheets = (
  queryKey: string,
  workerId: string | undefined,
  queryOptions: any
) => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery(
    [queryKey, workerId],
    async ({ queryKey }: any) => {
      const token = await getAccessTokenSilently()
      return await Api.get(`v1/users/timesheets/worker/${workerId}`, token)
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
    }
  )
}

export const useGetListItems = (key: string, value: any) => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery(
    [key, value],
    async ({ queryKey }: any) => {
      const token = await getAccessTokenSilently()
      return await Api.get(`v1/users/type/worker`, token)
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
    }
  )
}

export const useGetWorkers = (key: string, value: any) => {
  const { getAccessTokenSilently } = useAuth0()
  return useQuery(
    [key, value],
    async ({ queryKey }: any) => {
      const token = await getAccessTokenSilently()
      return await Api.get(`v1/users/workers` + value, token)
    },
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
      cacheTime: 0,
    }
  )
}

export const useMutateCreateUpdateWorker = (key: string) => {
  const uri = `users`
  return useMutateItemForWorker(key, uri)
}

export const useMutateUpdateTimeSheetForWorker = (
  key: string,
  timesheetId: string | undefined
) => {
  const uri = `users/timesheets`
  return useMutateItemForWorker(key, uri)
}

export const useChangeStatusWorkers = (key: any, payload: any) => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  // Use the useMutateDeleteItem hook
  const deleteItemMutation = useMutateDeleteUser(key)
  const mutation = useMutation(
    async (payload: any) => {
      const token = await getAccessTokenSilently()
      return await Api.post(`v1/users/workers/change-status`, payload, token)
    },
    {
      onSuccess: async () => {
        if (payload.itemsToDelete && Array.isArray(payload.itemsToDelete)) {
          await Promise.all(
            payload.itemsToDelete.map(async (id: any) => {
              await deleteItemMutation.mutateAsync(id)
            })
          )
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [key] }),
          queryClient.invalidateQueries({ queryKey: ['workers'] }),
        ])
      },
    }
  )

  return mutation
}

export const useMutateDeleteItem = (key: string) => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    async (id: any) => {
      const token = await getAccessTokenSilently()
      const response = await Api.delete(`v1/users/invitations/${id}`, token)
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [key] })
      },
    }
  )
  return mutation
}
export const useMutateDeleteUser = (key: string) => {
  const { getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    async (id: any) => {
      const token = await getAccessTokenSilently()
      const response = await Api.delete(`v1/users/${id}`, token)
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [key] })
      },
    }
  )
  return mutation
}
